import { supabase } from './supabase';
import { logger } from './logger';

// ─── Types fiche 360 candidat ────────────────────────────────────────

export interface CandidateExperience360 {
  id: string;
  companyName: string;
  city: string;
  sectorName: string;
  positionName: string;
  yearsExperience: string;
  workRhythm: string | null;
}

export interface CandidateVideo360 {
  id: string;
  thumbnailUrl: string | null;
  status: string;
  analysisVerdict: string | null;
  mediaType: string | null;
  createdAt: string;
}

export interface CandidateApplication360 {
  id: string;
  status: string;
  appliedAt: string;
  positionName: string;
  establishmentName: string;
  city: string;
  offerId: string;
}

export interface Candidate360 {
  profileId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  department: string;
  photoUrl: string | null;
  tagline: string;
  status: string;
  seedTag: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  profileComplete: boolean;
  identityVerified: boolean;
  hasDrivingLicense: boolean;
  mobilityMinutes: number | null;
  desiredWorkRhythm: string | null;
  desiredContractTypes: string[] | null;
  desiredSalaryMin: number | null;
  desiredSalaryMax: number | null;
  availabilityType: string | null;
  availabilityDate: string | null;
  languages: { language: string; level: number }[];
  experiences: CandidateExperience360[];
  videos: CandidateVideo360[];
  applications: CandidateApplication360[];
  stats: {
    profileViewsReceived: number;
    favoritesMade: number;
    swipesMade: number;
  };
}

// ─── Types fiche 360 recruteur ───────────────────────────────────────

export interface EmployerOffer360 {
  id: string;
  positionName: string;
  establishmentName: string;
  city: string;
  status: string;
  contractType: string | null;
  applicationsCount: number;
  publishedAt: string | null;
  createdAt: string;
}

export interface EmployerVideo360 {
  id: string;
  thumbnailUrl: string | null;
  status: string;
  title: string | null;
  videoType: string | null;
  createdAt: string;
}

export interface Employer360 {
  profileId: string;
  userId: string;
  companyName: string;
  siret: string;
  siretVerified: boolean;
  firstName: string;
  lastName: string;
  positionInCompany: string;
  email: string;
  phone: string;
  address: string;
  zipcode: string;
  city: string;
  employeeCount: string | null;
  status: string;
  seedTag: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  profileComplete: boolean;
  subscriptionActive: boolean;
  plan: 'free' | 'premium';
  stripeCustomerId: string | null;
  offers: EmployerOffer360[];
  videos: EmployerVideo360[];
  stats: {
    applicationsReceived: number;
    profileViewsMade: number;
    favoritesMade: number;
    activeOffers: number;
    candidatesContacted: number;
    videosWatched: number;
  };
}

// ─── Fetchers ────────────────────────────────────────────────────────

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
const fmtDateTime = (d: string | null) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : null;

export async function fetchCandidate360(profileId: string): Promise<Candidate360> {
  const { data: c, error } = await supabase
    .from('candidate_profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  if (error || !c) {
    logger.error('fetch_candidate_360', 'userDetailService', { profileId, error: error?.message });
    throw error || new Error('Candidat introuvable');
  }

  const [profileRes, expRes, langRes, videoRes, appRes, viewsRes, favRes, swipeRes] = await Promise.all([
    supabase.from('profiles').select('email, phone, status, seed_tag, last_login_at').eq('id', c.user_id).single(),
    supabase.from('candidate_experiences')
      .select('id, company_name, company_city, years_experience, work_rhythm, sector:sectors(name_fr), position:positions(name_fr)')
      .eq('candidate_id', profileId)
      .order('sort_order'),
    supabase.from('candidate_languages').select('language, level').eq('candidate_id', profileId),
    supabase.from('candidate_videos')
      .select('id, thumbnail_url, status, analysis_verdict, media_type, created_at')
      .eq('candidate_id', profileId)
      .order('created_at', { ascending: false }),
    supabase.from('applications')
      .select('id, status, applied_at, offer:job_offers(id, position_name, establishment_name, establishment_city)')
      .eq('candidate_id', profileId)
      .order('applied_at', { ascending: false }),
    supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('candidate_id', profileId),
    supabase.from('favorites').select('id', { count: 'exact', head: true }).eq('user_id', c.user_id),
    supabase.from('swipe_actions').select('id', { count: 'exact', head: true }).eq('user_id', c.user_id),
  ]);

  const p = profileRes.data;
  logger.technical('fetch_candidate_360_ok', 'userDetailService', { profileId });

  return {
    profileId: c.id,
    userId: c.user_id,
    firstName: c.first_name || '',
    lastName: c.last_name || '',
    email: p?.email || '',
    phone: p?.phone || '',
    city: c.city || '',
    department: c.department || '',
    photoUrl: c.photo_url,
    tagline: c.tagline_fr || c.tagline || '',
    status: p?.status || 'pending_verification',
    seedTag: p?.seed_tag || null,
    registeredAt: fmtDate(c.created_at),
    lastLoginAt: fmtDateTime(p?.last_login_at || null),
    profileComplete: c.profile_complete || false,
    identityVerified: c.identity_verified || false,
    hasDrivingLicense: c.has_driving_license || false,
    mobilityMinutes: c.mobility_minutes,
    desiredWorkRhythm: c.desired_work_rhythm,
    desiredContractTypes: c.desired_contract_types,
    desiredSalaryMin: c.desired_salary_min,
    desiredSalaryMax: c.desired_salary_max,
    availabilityType: c.availability_type,
    availabilityDate: c.availability_date ? fmtDate(c.availability_date) : null,
    languages: (langRes.data || []).map((l: any) => ({ language: l.language, level: l.level })),
    experiences: (expRes.data || []).map((e: any) => ({
      id: e.id,
      companyName: e.company_name || '',
      city: e.company_city || '',
      sectorName: e.sector?.name_fr || '',
      positionName: e.position?.name_fr || '',
      yearsExperience: e.years_experience || '',
      workRhythm: e.work_rhythm,
    })),
    videos: (videoRes.data || []).map((v: any) => ({
      id: v.id,
      thumbnailUrl: v.thumbnail_url,
      status: v.status,
      analysisVerdict: v.analysis_verdict,
      mediaType: v.media_type,
      createdAt: fmtDate(v.created_at),
    })),
    applications: (appRes.data || []).map((a: any) => ({
      id: a.id,
      status: a.status,
      appliedAt: fmtDate(a.applied_at),
      positionName: a.offer?.position_name || '',
      establishmentName: a.offer?.establishment_name || '',
      city: a.offer?.establishment_city || '',
      offerId: a.offer?.id || '',
    })),
    stats: {
      profileViewsReceived: viewsRes.count || 0,
      favoritesMade: favRes.count || 0,
      swipesMade: swipeRes.count || 0,
    },
  };
}

export async function fetchEmployer360(profileId: string): Promise<Employer360> {
  const { data: e, error } = await supabase
    .from('employer_profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  if (error || !e) {
    logger.error('fetch_employer_360', 'userDetailService', { profileId, error: error?.message });
    throw error || new Error('Recruteur introuvable');
  }

  const [profileRes, offersRes, videosRes, viewsRes, favRes, selectionsRes, videoViewsRes, subRes] = await Promise.all([
    supabase.from('profiles').select('email, phone, status, seed_tag, last_login_at').eq('id', e.user_id).single(),
    supabase.from('job_offers')
      .select('id, position_name, establishment_name, establishment_city, status, contract_type, applications_count, published_at, created_at')
      .eq('employer_id', profileId)
      .order('created_at', { ascending: false }),
    supabase.from('employer_videos')
      .select('id, thumbnail_url, status, title, video_type, created_at')
      .eq('employer_id', profileId)
      .order('created_at', { ascending: false }),
    supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('employer_id', profileId),
    supabase.from('favorites').select('id', { count: 'exact', head: true }).eq('user_id', e.user_id),
    supabase.from('employer_selections').select('id', { count: 'exact', head: true }).eq('employer_id', profileId),
    supabase.from('video_interactions').select('id', { count: 'exact', head: true }).eq('user_id', e.user_id).eq('interaction', 'view'),
    supabase.from('subscriptions').select('plan, status').eq('employer_id', profileId).order('created_at', { ascending: false }),
  ]);

  const p = profileRes.data;
  const offers = (offersRes.data || []).map((o: any) => ({
    id: o.id,
    positionName: o.position_name || '',
    establishmentName: o.establishment_name || '',
    city: o.establishment_city || '',
    status: o.status,
    contractType: o.contract_type,
    applicationsCount: o.applications_count || 0,
    publishedAt: o.published_at ? fmtDate(o.published_at) : null,
    createdAt: fmtDate(o.created_at),
  }));
  logger.technical('fetch_employer_360_ok', 'userDetailService', { profileId });

  return {
    profileId: e.id,
    userId: e.user_id,
    companyName: e.company_name || '',
    siret: e.siret || '',
    siretVerified: e.siret_verified || false,
    firstName: e.first_name || '',
    lastName: e.last_name || '',
    positionInCompany: e.position_in_company || '',
    email: p?.email || e.email || '',
    phone: p?.phone || e.phone || '',
    address: e.company_address || '',
    zipcode: e.company_zipcode || '',
    city: e.company_city || '',
    employeeCount: e.employee_count,
    status: p?.status || 'pending_verification',
    seedTag: p?.seed_tag || null,
    registeredAt: fmtDate(e.created_at),
    lastLoginAt: fmtDateTime(p?.last_login_at || null),
    profileComplete: e.profile_complete || false,
    subscriptionActive: e.subscription_active || false,
    plan: (subRes.data || []).some((s: any) => s.plan === 'premium' && s.status === 'active') ? 'premium' : 'free',
    stripeCustomerId: e.stripe_customer_id,
    offers,
    videos: (videosRes.data || []).map((v: any) => ({
      id: v.id,
      thumbnailUrl: v.thumbnail_url,
      status: v.status,
      title: v.title,
      videoType: v.video_type,
      createdAt: fmtDate(v.created_at),
    })),
    stats: {
      applicationsReceived: offers.reduce((sum, o) => sum + o.applicationsCount, 0),
      profileViewsMade: viewsRes.count || 0,
      favoritesMade: favRes.count || 0,
      activeOffers: offers.filter((o) => o.status === 'active').length,
      candidatesContacted: selectionsRes.count || 0,
      videosWatched: videoViewsRes.count || 0,
    },
  };
}

// ─── Comptes admin ───────────────────────────────────────────────────

export interface AdminAccount {
  userId: string;
  email: string;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export async function fetchAdminAccounts(): Promise<AdminAccount[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, status, created_at, last_login_at')
    .eq('role', 'admin')
    .order('created_at', { ascending: true });
  if (error) {
    logger.error('fetch_admin_accounts', 'userDetailService', { error: error.message });
    throw error;
  }
  return (data || []).map((p: any) => ({
    userId: p.id,
    email: p.email || '',
    status: p.status || 'active',
    createdAt: fmtDate(p.created_at),
    lastLoginAt: fmtDateTime(p.last_login_at),
  }));
}

export async function createAdminAccount(email: string, password: string): Promise<void> {
  logger.functional('create_admin_account', 'userDetailService', { email });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-admin`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ email, password }),
    }
  );
  const body = await res.json();
  if (!res.ok) {
    logger.error('create_admin_account', 'userDetailService', { email, error: body.error });
    throw new Error(body.error || 'Création échouée');
  }
  logger.technical('create_admin_account_ok', 'userDetailService', { email });
}
